import { Fragment, useMemo } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import { getAccountDispositionStatusParams } from "shared/lib/helpers/disposition";
import { clsxMerge, fixURL } from "shared/lib/helpers";
import { DynamicTextSize } from "shared/ui/dynamic-text-size";
import { formatMoneyDollars, formatNumbers } from "shared/lib/helpers/numbers";
import { AccountDetailsStatusI, AccountI } from "shared/lib/interfaces/account";

import {
  LabelValue,
  createRecord,
} from "@/modules/pipeline/account-details/shared/label-value";
import { AccountDispositionStatusSmallPill } from "@/modules/pipeline/account-details/shared/status-pill";
import { HorizontalDivider } from "./horizontal-diviider";

export const AccountTopSectionContent = ({
  account,
  accountStatus,
}: {
  account?: AccountI;
  accountStatus?: AccountDetailsStatusI;
}) => {
  const accountInfoItems = [
    createRecord(
      "Revenue",
      account?.revenue
        ? `${formatMoneyDollars(account.revenue, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`
        : null
    ),
    createRecord("HQ State", account?.hq_state || null),
    createRecord(
      "Employees",
      account?.employee_count
        ? `${formatNumbers(account?.employee_count)}`
        : null
    ),
    createRecord("Industry", account?.industry || null),
  ];

  const accountStatusBadge = useMemo(
    () => (
      <AccountDispositionStatusSmallPill
        {...getAccountDispositionStatusParams(accountStatus, true)}
      />
    ),
    [accountStatus?.status]
  );

  const linkProps = useMemo(() => {
    return account?.website
      ? {
          target: "_blank",
          href: fixURL(account?.website),
          rel: "noreferrer",
        }
      : {};
  }, [account?.website]);

  return (
    <div className="px-6 pt-6">
      <a
        className={clsxMerge(
          "mb-1 w-full typography-header-5-semibold",
          "flex items-center",
          {
            "hover:underline": account?.website,
          }
        )}
        {...linkProps}
      >
        <DynamicTextSize
          text={account?.name}
          initialFontSize={32}
          minFontSize={14}
        />

        {account?.website && (
          <span>
            <ArrowTopRightOnSquareIcon className="ml-2 w-5 text-[#4474E3]" />
          </span>
        )}
      </a>

      <div className="flex w-full items-end gap-x-3">
        <LabelValue
          label="Status"
          value={accountStatusBadge}
          valueClassName="mb-0"
        />

        <HorizontalDivider />

        {accountInfoItems?.map((record, i) => (
          <Fragment key={i}>
            <LabelValue {...record} className="max-w-[25%]" />
            {record?.value && <HorizontalDivider />}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
